import { chunk, range, uniqBy } from 'lodash';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { directoryAxiosClient } from 'Services/Api/client';
import DIRECTORY_API from 'Services/Api/Directory/Api';
import {
	DirectoryGroup,
	DirectoryRole,
	DirectoryUser,
	GetUsersParams,
	GroupsPageResponse,
	UsersPageParams,
	UsersPageResponse
} from 'Services/Api/Directory/Types';
import { Status } from 'app-types';
import { throttledRequest } from 'LearningManager/helpers/concurrency';
import {
	FILTER_PAGE_SIZE,
	PageResult,
	PagedList
} from 'LearningManager/helpers/pagedList';

const USERS_PAGE_SIZE = 200;
const MAX_USER_PAGES = 100;
const MAX_LISTED_USERS = USERS_PAGE_SIZE * MAX_USER_PAGES;

export interface PropertyScopeOptions {
	allProperties?: boolean;
	tenantId?: number;
}
const FIRST_USERS_PAGE = 1;
const GROUP_IDS_CHUNK = 50;

class AssignUsersStore {
	status = {
		fetchOrganizations: Status.Idle,
		fetchAssignmentOrganizations: Status.Idle,
		fetchPropertiesByTenant: Status.Idle,
		fetchRoles: Status.Idle,
		fetchGroups: Status.Idle,
		fetchUsers: Status.Idle,
		fetchMoreUsers: Status.Idle,
		fetchAllUsers: Status.Idle,
		fetchUsersByIds: Status.Idle,
		fetchFilterUsers: Status.Idle
	};

	organizations: DirectoryGroup[] = [];
	// The organizations an assignment can target. Isolated from `organizations`
	// — that list is every tenant, and it backs the tenant picker itself, so
	// narrowing the create flow to one tenant must not shrink the picker that
	// chose it.
	assignmentOrganizations: DirectoryGroup[] = [];
	// Isolated from `pagedProperties` so the two pickers never overwrite each other:
	// this one always belongs to one named tenant (the Save Report modal), while
	// `pagedProperties` follows whatever the create flow is currently scoped to.
	tenantProperties: DirectoryGroup[] = [];
	roles: DirectoryRole[] = [];
	groups: DirectoryGroup[] = [];
	users: DirectoryUser[] = [];
	usersTotal = 0;
	hasMoreUsers = false;
	usersPropertyIds: number[] = [];
	usersSearch = '';
	usersScope: PropertyScopeOptions = {};
	usersNextPage = FIRST_USERS_PAGE;
	usersLoadPromise: Promise<void> | null = null;
	allUsersPromise: Promise<DirectoryUser[] | undefined> | null = null;
	systemGroupIds:number[] = [];
	// Isolated from `users` so the create-flow property picker is never affected.
	assignmentUsers: DirectoryUser[] = [];
	filterUsers: DirectoryUser[] = [];
	filterUsersTenantId: number | undefined = undefined;
	usersRequestId = 0;
	usersTotalKnown = false;
	assignmentUsersRequestId = 0;
	filterUsersRequestId = 0;
	assignmentOrganizationsRequestId = 0;
	groupsRequestId = 0;
	pagedProperties = new PagedList<DirectoryGroup>(
		(tenantId, page, limit, search) =>
			this.fetchPropertiesPage(tenantId, page, limit, search)
	);
	pagedUsers = new PagedList<DirectoryUser>((tenantId, page, limit, search) =>
		this.fetchTenantUsersPage(tenantId, page, limit, search)
	);

	constructor() {
		makeObservable(this, {
			status: observable,
			organizations: observable,
			assignmentOrganizations: observable,
			tenantProperties: observable,
			roles: observable,
			groups: observable,
			users: observable,
			usersTotal: observable,
			hasMoreUsers: observable,
			assignmentUsers: observable,
			filterUsers: observable
		});
	}

	@action
	async fetchOrganizations(): Promise<void> {
		runInAction(() => {
			this.status.fetchOrganizations = Status.Pending;
		});
		try {
			const { data } = await directoryAxiosClient.get<DirectoryGroup[]>(
				`${DIRECTORY_API.GROUPS}?label[]=Organization`
			);
			runInAction(() => {
				this.organizations = data;
				this.status.fetchOrganizations = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.fetchOrganizations = Status.Failure;
			});
			throw e;
		}
	}

	/**
	 * The organizations a new assignment can target. With `tenantId` the list is
	 * that one tenant's organization; without it the caller's own token decides
	 * what comes back.
	 */
	@action
	async fetchAssignmentOrganizations(tenantId?: number, tenantName?: string, systemGroupIds?: number[], nonTenantUser?: boolean): Promise<void> {
		const requestId = (this.assignmentOrganizationsRequestId += 1);
		runInAction(() => {
			this.status.fetchAssignmentOrganizations = Status.Pending;
		});
		try {
			const { data } = await directoryAxiosClient.get<DirectoryGroup[]>(
				tenantId
					? `${DIRECTORY_API.GROUPS}?id=${tenantId}&include_group_tree=true`
					: `${DIRECTORY_API.GROUPS}?label[]=Organization`
			);
			if (requestId !== this.assignmentOrganizationsRequestId) return;
			runInAction(() => {
			const organization = data.find(
				(org) => org.label === 'Organization'
			);

			const totalUserCount = data.reduce(
				(total, org) => total + (org.userCount ?? 0),
				0
			);

			this.assignmentOrganizations = organization
				? [
						{
							...organization,
							userCount: totalUserCount
						}
					]
				: [
						{
							id: Number(tenantId),
							name: tenantName ?? '',
							parentId: Number(tenantId),
							tenantId: Number(tenantId),
							isProperty: false,
							isTenant: nonTenantUser ? false : true,
							label: tenantName ?? '',
							userCount: totalUserCount
						}
					];

			this.status.fetchAssignmentOrganizations = Status.Success;
		});
		} catch (e) {
			if (requestId !== this.assignmentOrganizationsRequestId) return;
			runInAction(() => {
				this.status.fetchAssignmentOrganizations = Status.Failure;
			});
			throw e;
		}
	}

	// One tenant's properties, addressed the way fetchGroupsByProperty walks
	// down from its parent group.
	@action
	async fetchPropertiesByTenant(tenantId: number): Promise<void> {
		if (!tenantId) {
			runInAction(() => {
				this.tenantProperties = [];
			});
			return;
		}
		runInAction(() => {
			this.status.fetchPropertiesByTenant = Status.Pending;
		});
		try {
			const { data } = await directoryAxiosClient.get<DirectoryGroup[]>(
				`${DIRECTORY_API.GROUPS}?id=${tenantId}&include_group_tree=true&label[]=Property`
			);
			runInAction(() => {
				this.tenantProperties = data;
				this.status.fetchPropertiesByTenant = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.fetchPropertiesByTenant = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async fetchRoles(): Promise<void> {
		runInAction(() => {
			this.status.fetchRoles = Status.Pending;
		});
		try {
			const { data } = await directoryAxiosClient.get<DirectoryRole[]>(
				DIRECTORY_API.ROLES
			);
			runInAction(() => {
				this.roles = data;
				this.status.fetchRoles = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.fetchRoles = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async fetchGroupsByProperty(
		propertyIds: number[],
		{ allProperties = false, tenantId }: PropertyScopeOptions = {}
	): Promise<void> {
		const requestId = (this.groupsRequestId += 1);
		if (!allProperties && !propertyIds.length) {
			runInAction(() => {
				this.groups = [];
				this.status.fetchGroups = Status.Idle;
			});
			return;
		}
		runInAction(() => {
			this.status.fetchGroups = Status.Pending;
		});
		try {
			const data = allProperties
				? await this.fetchTenantGroups(tenantId)
				: await this.fetchGroupsInChunks(
						propertyIds,
						'&include_group_tree=true&label[]=Group'
				  );
			if (requestId !== this.groupsRequestId) return;
			runInAction(() => {
				this.groups = data;
				this.status.fetchGroups = Status.Success;
			});
		} catch (e) {
			if (requestId !== this.groupsRequestId) return;
			runInAction(() => {
				this.status.fetchGroups = Status.Failure;
			});
			throw e;
		}
	}

	private async fetchTenantGroups(
		tenantId: number | undefined
	): Promise<DirectoryGroup[]> {
		const { data } = await throttledRequest(() =>
			directoryAxiosClient.get<DirectoryGroup[]>(
				tenantId
					? `${DIRECTORY_API.GROUPS}?id=${tenantId}&include_group_tree=true&label[]=Group`
					: `${DIRECTORY_API.GROUPS}?label[]=Group`
			)
		);
		return data;
	}

	private async fetchPropertiesPage(
		tenantId: number | undefined,
		page: number,
		limit: number,
		search: string
	): Promise<PageResult<DirectoryGroup>> {
		const scope = tenantId
			? `id=${tenantId}&include_group_tree=true&label[]=Property`
			: 'label[]=Property';
		const searchQuery = search ? `&name=${encodeURIComponent(search)}` : '';
		const { data } = await throttledRequest(() =>
			directoryAxiosClient.get<GroupsPageResponse | DirectoryGroup[]>(
				`${DIRECTORY_API.GROUPS}?${scope}&page=${page}&limit=${limit}${searchQuery}`
			)
		);
		if (Array.isArray(data)) {
			return { items: data, hasMore: data.length === limit };
		}
		const items = data?.data ?? [];
		const totalPages = data?.pagination?.totalPages;
		return {
			items,
			hasMore:
				totalPages !== undefined
					? page < totalPages
					: items.length === limit
		};
	}

	private async fetchTenantUsersPage(
		tenantId: number | undefined,
		page: number,
		limit: number,
		search: string
	): Promise<PageResult<DirectoryUser>> {
		const { users, total } = await this.fetchUsersPage(
			{
				...(tenantId
					? { tenant_id: [tenantId], include_group_tree: true }
					: {}),
				...(search ? { name: search } : {})
			},
			page,
			limit
		);
		return {
			items: users,
			hasMore:
				total !== undefined
					? page * limit < total
					: users.length === limit
		};
	}

	private async fetchUsersPage(
		params: GetUsersParams,
		page: number,
		limit = USERS_PAGE_SIZE
	): Promise<{ users: DirectoryUser[]; total?: number }> {
		const pageParams: GetUsersParams & UsersPageParams = {
			...params,
			page,
			limit
		};
		const { data } = await throttledRequest(() =>
			directoryAxiosClient.get<UsersPageResponse | DirectoryUser[]>(
				DIRECTORY_API.USERS,
				{ params: pageParams }
			)
		);

		// Paginating this endpoint moved it onto the envelope the rest of
		// the API uses; the other directory lists still answer with a plain
		// array, so both are read here.
		return {
			users: Array.isArray(data) ? data : data?.data ?? [],
			total: Array.isArray(data) ? undefined : data?.pagination?.total
		};
	}

	private async fetchUserPages(
		params: GetUsersParams
	): Promise<DirectoryUser[]> {
		const first = await this.fetchUsersPage(params, FIRST_USERS_PAGE);
		if (first.users.length !== USERS_PAGE_SIZE) return first.users;
		if (first.total === undefined) {
			return this.fetchUserPagesInOrder(params, first.users);
		}

		const pages = await this.fetchUserPageRange(
			params,
			FIRST_USERS_PAGE + 1,
			first.total
		);
		return uniqBy(
			first.users.concat(...pages.map((page) => page.users)),
			'id'
		);
	}

	private fetchUserPageRange(
		params: GetUsersParams,
		fromPage: number,
		total: number
	): Promise<{ users: DirectoryUser[]; total?: number }[]> {
		const lastPage = Math.min(
			Math.ceil(total / USERS_PAGE_SIZE),
			FIRST_USERS_PAGE + MAX_USER_PAGES - 1
		);
		return Promise.all(
			range(fromPage, lastPage + 1).map((page) =>
				this.fetchUsersPage(params, page)
			)
		);
	}

	private async fetchUserPagesInOrder(
		params: GetUsersParams,
		firstPage: DirectoryUser[]
	): Promise<DirectoryUser[]> {
		const users = [...firstPage];

		for (let index = 1; index < MAX_USER_PAGES; index += 1) {
			const { users: page, total } = await this.fetchUsersPage(
				params,
				FIRST_USERS_PAGE + index
			);

			users.push(...page);
			if (page.length !== USERS_PAGE_SIZE) break;
			if (total !== undefined && users.length >= total) break;
		}

		return users;
	}

	private propertyUsersParams(): GetUsersParams {
		const { allProperties, tenantId } = this.usersScope;
		const scope: GetUsersParams = allProperties
			? tenantId
				? { tenant_id: this.systemGroupIds.length > 0 ? this.systemGroupIds : [tenantId], include_group_tree: true }
				: {}
			: {
					group_id: this.usersPropertyIds.join(','),
					include_group_tree: true
			  };
		return {
			...scope,
			...(this.usersSearch ? { name: this.usersSearch } : {})
		};
	}

	private async loadNextUsersPage(requestId: number): Promise<void> {
		const page = this.usersNextPage;
		const { users, total } = await this.fetchUsersPage(
			this.propertyUsersParams(),
			page,
			FILTER_PAGE_SIZE
		);
		if (requestId !== this.usersRequestId) return;

		const knownIds = new Set(this.users.map((user) => user.id));
		const loaded =
			page === FIRST_USERS_PAGE
				? users
				: [
						...this.users,
						...users.filter((user) => !knownIds.has(user.id))
				  ];
		const hasMore =
			users.length === FILTER_PAGE_SIZE &&
			loaded.length < MAX_LISTED_USERS &&
			(total === undefined || loaded.length < total);

		this.usersNextPage = page + 1;
		this.usersTotalKnown = total !== undefined;
		runInAction(() => {
			this.users = loaded;
			this.usersTotal = total ?? loaded.length;
			this.hasMoreUsers = hasMore;
		});
	}

	private async loadUserPagesInParallel(requestId: number): Promise<void> {
		const pages = await this.fetchUserPageRange(
			this.propertyUsersParams(),
			FIRST_USERS_PAGE,
			this.usersTotal
		);
		if (requestId !== this.usersRequestId) return;

		const loaded = uniqBy(
			this.users.concat(...pages.map((page) => page.users)),
			'id'
		);
		runInAction(() => {
			this.users = loaded;
			this.hasMoreUsers = false;
		});
	}

	private async loadUsersPageWithStatus(
		requestId: number,
		statusKey: 'fetchUsers' | 'fetchMoreUsers'
	): Promise<void> {
		runInAction(() => {
			this.status[statusKey] = Status.Pending;
		});
		try {
			await this.loadNextUsersPage(requestId);
			if (requestId !== this.usersRequestId) return;
			runInAction(() => {
				this.status[statusKey] = Status.Success;
			});
		} catch (e) {
			if (requestId !== this.usersRequestId) return;
			runInAction(() => {
				this.status[statusKey] = Status.Failure;
			});
			throw e;
		}
	}

	private runUsersLoad(
		requestId: number,
		statusKey: 'fetchUsers' | 'fetchMoreUsers'
	): Promise<void> {
		const load = this.loadUsersPageWithStatus(requestId, statusKey);
		const clear = () => {
			if (this.usersLoadPromise === load) this.usersLoadPromise = null;
		};
		this.usersLoadPromise = load;
		load.then(clear, clear);
		return load;
	}

	@action
	async fetchUsersByProperty(
		propertyIds: number[],
		search = '',
		scope: PropertyScopeOptions = {},
		systemGroupIds: number[]
	): Promise<void> {
		const requestId = (this.usersRequestId += 1);
		this.usersPropertyIds = propertyIds;
		this.usersScope = scope;
		this.usersSearch = search.trim();
		this.usersNextPage = FIRST_USERS_PAGE;
		this.usersTotalKnown = false;
		this.usersLoadPromise = null;
		this.allUsersPromise = null;
		this.systemGroupIds = systemGroupIds;

		runInAction(() => {
			this.hasMoreUsers = false;
			this.status.fetchMoreUsers = Status.Idle;
			this.status.fetchAllUsers = Status.Idle;
		});

		if (!scope.allProperties && !propertyIds.length) {
			runInAction(() => {
				this.users = [];
				this.usersTotal = 0;
			});
			return;
		}
		return this.runUsersLoad(requestId, 'fetchUsers');
	}

	@action
	fetchNextUsersPage(): Promise<void> {
		if (this.usersLoadPromise) return this.usersLoadPromise;
		if (!this.hasMoreUsers) return Promise.resolve();
		return this.runUsersLoad(this.usersRequestId, 'fetchMoreUsers');
	}

	private async loadRemainingUsers(
		requestId: number
	): Promise<DirectoryUser[] | undefined> {
		runInAction(() => {
			this.status.fetchAllUsers = Status.Pending;
		});
		try {
			if (this.usersLoadPromise) await this.usersLoadPromise;
			if (
				requestId === this.usersRequestId &&
				this.hasMoreUsers &&
				this.usersTotalKnown
			) {
				const load = this.loadUserPagesInParallel(requestId);
				const clear = () => {
					if (this.usersLoadPromise === load) {
						this.usersLoadPromise = null;
					}
				};
				this.usersLoadPromise = load;
				load.then(clear, clear);
				await load;
			}
			while (requestId === this.usersRequestId && this.hasMoreUsers) {
				await this.fetchNextUsersPage();
			}
			if (requestId !== this.usersRequestId) return undefined;
			runInAction(() => {
				this.status.fetchAllUsers = Status.Success;
			});
			return this.users;
		} catch (e) {
			if (requestId !== this.usersRequestId) return undefined;
			runInAction(() => {
				this.status.fetchAllUsers = Status.Failure;
			});
			throw e;
		}
	}

	@action
	fetchAllUsers(): Promise<DirectoryUser[] | undefined> {
		if (this.allUsersPromise) return this.allUsersPromise;

		const load = this.loadRemainingUsers(this.usersRequestId);
		const clear = () => {
			if (this.allUsersPromise === load) this.allUsersPromise = null;
		};
		this.allUsersPromise = load;
		load.then(clear, clear);
		return load;
	}

	/**
	 * The ids of everyone under the given groups — properties, organizations and
	 * groups are all directory groups, so one query covers them. Used where an
	 * endpoint takes user_ids but the user picked whole groups.
	 */
	@action
	async fetchUserIdsByGroups(groupIds: number[]): Promise<string[]> {
		if (!groupIds.length) return [];
		const pages = await Promise.all(
			[...new Set(groupIds)].map((groupId) =>
				this.fetchUserPages({
					group_id: String(groupId),
					include_group_tree: true
				})
			)
		);
		return [
			...new Set(
				([] as string[]).concat(
					...pages.map((users) => users.map((user) => user.id))
				)
			)
		];
	}

	@action
	async fetchUserIdsByGroupLabels(
		tenantId: number,
		labels: string[]
	): Promise<string[]> {
		if (!labels.length) return [];
		const users = await this.fetchUserPages({
			tenant_id: [tenantId],
			group_label: labels,
			include_group_tree: true
		});
		return users.map((user) => user.id);
	}

	@action
	async fetchUserIdsByRoles(
		tenantId: number,
		roleIds: number[]
	): Promise<string[]> {
		if (!roleIds.length) return [];
		const users = await this.fetchUserPages({
			tenant_id: [tenantId],
			role_id: roleIds
		});
		return users.map((user) => user.id);
	}

	@action
	async fetchGroupsByIds(groupIds: number[]): Promise<DirectoryGroup[]> {
		if (!groupIds.length) return [];
		return this.fetchGroupsInChunks(groupIds);
	}

	private async fetchGroupsInChunks(
		groupIds: number[],
		query = ''
	): Promise<DirectoryGroup[]> {
		const pages = await Promise.all(
			chunk([...new Set(groupIds)], GROUP_IDS_CHUNK).map((ids) =>
				throttledRequest(() =>
					directoryAxiosClient.get<DirectoryGroup[]>(
						`${DIRECTORY_API.GROUPS}?id=${ids.join(',')}${query}`
					)
				).then(({ data }) => data)
			)
		);
		return uniqBy(([] as DirectoryGroup[]).concat(...pages), 'id');
	}

	@action
	async fetchFilterUsers(tenantId?: number): Promise<void> {
		const requestId = (this.filterUsersRequestId += 1);

		runInAction(() => {
			this.status.fetchFilterUsers = Status.Pending;
		});
		try {
			const users = await this.fetchUserPages(
				tenantId
					? { tenant_id: [tenantId], include_group_tree: true }
					: {}
			);
			if (requestId !== this.filterUsersRequestId) return;
			runInAction(() => {
				this.filterUsers = users;
				this.filterUsersTenantId = tenantId;
				this.status.fetchFilterUsers = Status.Success;
			});
		} catch (e) {
			if (requestId !== this.filterUsersRequestId) throw e;
			runInAction(() => {
				this.status.fetchFilterUsers = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async fetchUsersByIds(userIds: string[]): Promise<void> {
		const requestId = (this.assignmentUsersRequestId += 1);

		if (!userIds.length) {
			runInAction(() => {
				this.assignmentUsers = [];
			});
			return;
		}
		runInAction(() => {
			this.status.fetchUsersByIds = Status.Pending;
		});
		try {
			const users = await this.fetchUserPages({
				id: userIds.join(','),
				include_group_tree: false
			});
			if (requestId !== this.assignmentUsersRequestId) return;
			runInAction(() => {
				this.assignmentUsers = users;
				this.status.fetchUsersByIds = Status.Success;
			});
		} catch (e) {
			if (requestId !== this.assignmentUsersRequestId) throw e;
			runInAction(() => {
				this.status.fetchUsersByIds = Status.Failure;
			});
			throw e;
		}
	}
}

export const assignUsersStore = new AssignUsersStore();
export default AssignUsersStore;
